import { useQuery } from "@tanstack/react-query";
import { API_BASE, ApiError, api } from "../../api/client";
import type { Problem } from "../../api/types";

export interface AssistantStatus {
  enabled: boolean;
  provider: string | null;
  model: string | null;
  reason: string;
}

export function useAssistantStatus() {
  return useQuery({
    queryKey: ["assistant-status"],
    queryFn: () => api.get<AssistantStatus>("/assistant/status"),
    staleTime: 60_000,
  });
}

export interface FileProposal {
  id: string;
  kind: "file";
  path: string;
  file_kind: string;
  summary: string;
  content: string;
  original: string | null;
  exists: boolean;
  problems: Problem[];
  diff: string;
  applied: boolean;
  decided?: boolean;
}

export interface FrameParameterProposal {
  id: string;
  kind: "frame_parameter";
  frame: string;
  frame_file: string;
  is_prm: boolean;
  mnemonic: string;
  parameter: Record<string, unknown>;
  original: Record<string, unknown> | null;
  exists: boolean;
  summary: string;
  problems: Problem[];
  applied: boolean;
  decided?: boolean;
}

export type Proposal = FileProposal | FrameParameterProposal;

export interface ToolActivity {
  id: string;
  name: string;
  input: Record<string, unknown>;
  ok?: boolean;
  summary?: string;
  done: boolean;
}

export interface ChatContext {
  route?: string | null;
  active_file?: string | null;
  active_flight?: string | null;
  selected_flights?: string[];
  selection?: string | null;
}

export interface AppliedDecision {
  id: string;
  applied: boolean;
  path?: string | null;
  mnemonic?: string | null;
}

export interface ChatRequest {
  project: string;
  message: string;
  conversation_id?: string | null;
  context?: ChatContext;
  history?: { role: "user" | "assistant"; content: string }[];
  applied?: AppliedDecision[];
}

export type ChatEvent =
  | { event: "start"; data: { conversation_id: string; provider: string; model: string; resumed: boolean } }
  | { event: "text"; data: { delta: string } }
  | { event: "tool_start"; data: { id: string; name: string; input: Record<string, unknown> } }
  | { event: "tool_end"; data: { id: string; name: string; ok: boolean; summary: string } }
  | { event: "proposal"; data: Proposal }
  | { event: "notice"; data: { message: string } }
  | { event: "error"; data: { message: string; fatal: boolean } }
  | {
      event: "done";
      data: { conversation_id: string; stop_reason: string | null; steps: number; usage: Record<string, number>; proposals: string[] };
    };

/** POST the turn and read the SSE stream; resolves when the server closes it. */
export async function streamChat(body: ChatRequest, onEvent: (e: ChatEvent) => void, signal?: AbortSignal): Promise<void> {
  const res = await fetch(`${API_BASE}/assistant/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    let detail: unknown = await res.text();
    try {
      detail = JSON.parse(detail as string).detail ?? detail;
    } catch {
      /* plain text */
    }
    throw new ApiError(res.status, detail);
  }
  if (!res.body) throw new ApiError(500, "no response body");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const dispatch = (chunk: string) => {
    let event = "message";
    const dataLines: string[] = [];
    for (const line of chunk.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
    }
    if (!dataLines.length) return;
    try {
      onEvent({ event, data: JSON.parse(dataLines.join("\n")) } as ChatEvent);
    } catch {
      /* ignore malformed frame */
    }
  };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    buffer = buffer.replace(/\r\n/g, "\n");
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      dispatch(buffer.slice(0, idx));
      buffer = buffer.slice(idx + 2);
    }
  }
  if (buffer.trim()) dispatch(buffer);
}

export const QUICK_PROMPTS: { label: string; prompt: string }[] = [
  {
    label: "New trigger",
    prompt:
      "Create a trigger that fires when … (describe the condition, e.g. 'both engines N1 above 85 % for 5 s while on ground'). Put its thresholds in inputs and bind it to a report.",
  },
  {
    label: "Derived parameter",
    prompt: "Add a derived parameter to the dataframe: … (e.g. 'average N1 of both engines' or 'altitude rate in ft/min from ALT').",
  },
  {
    label: "New report",
    prompt: "Create a CSV report for the … trigger with these values: … (mnemonics, aggregation). One row per snapshot.",
  },
  {
    label: "Tune thresholds",
    prompt: "Look at the active flight and suggest thresholds for the inputs used by the open trigger; explain the choices with actual values.",
  },
  {
    label: "Explain this file",
    prompt: "Explain what the open file does, step by step, and point out anything that could misfire or fail on missing data.",
  },
  {
    label: "Why did it not fire?",
    prompt: "Run the open trigger on the active flight and explain why it did (or did not) fire, using parameter values around the expected time.",
  },
];
